// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import VoiceParticipantManager from '@app/features/voice/engine/VoiceParticipantManager';
import {
	getRemoteSpeakingThresholdRms,
	SPEAKING_REMOTE_ATTACK_MS,
	SPEAKING_REMOTE_RELEASE_MS,
} from '@app/features/voice/engine/VoiceSpeakingThreshold';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import type {Participant, RemoteAudioTrack, RemoteTrack, RemoteTrackPublication, Room} from 'livekit-client';
import {Track} from 'livekit-client';

const logger = new Logger('VoiceRemoteSpeakingLevelManager');
interface AnalyserHandle {
	identity: string;
	track: MediaStreamTrack;
	source: MediaStreamAudioSourceNode;
	analyser: AnalyserNode;
	samples: Uint8Array<ArrayBuffer>;
	rafId: number | null;
	speaking: boolean;
	belowSinceMs: number | null;
	aboveSinceMs: number | null;
}
class VoiceRemoteSpeakingLevelManager {
	private audioContext: AudioContext | null = null;
	private analysers = new Map<string, AnalyserHandle>();
	private analyserSuspendedByVisibility = false;
	private installedVisibilityListener = false;
	private roomForRehydrate: Room | null = null;
	private detachAllAnalysers(): void {
		for (const handle of this.analysers.values()) {
			this.disposeHandle(handle);
			VoiceParticipantManager.setAudioLevelSpeaking(handle.identity, false);
		}
		this.analysers.clear();
	}
	private ensureVisibilityListener(): void {
		if (this.installedVisibilityListener) return;
		if (typeof document === 'undefined') return;
		document.addEventListener('visibilitychange', () => {
			if (document.hidden) {
				if (this.analyserSuspendedByVisibility) return;
				this.analyserSuspendedByVisibility = true;
				this.detachAllAnalysers();
			} else {
				if (!this.analyserSuspendedByVisibility) return;
				this.analyserSuspendedByVisibility = false;
				const room = this.roomForRehydrate;
				if (room) this.hydrateFromRoom(room);
			}
		});
		this.installedVisibilityListener = true;
	}
	private getAudioContext(): AudioContext | null {
		if (typeof window === 'undefined') return null;
		if (this.audioContext && this.audioContext.state !== 'closed') {
			return this.audioContext;
		}
		const AudioContextCtor =
			window.AudioContext ||
			(
				window as typeof window & {
					webkitAudioContext?: typeof AudioContext;
				}
			).webkitAudioContext;
		if (!AudioContextCtor) return null;
		try {
			this.audioContext = new AudioContextCtor({latencyHint: 'interactive'});
			if (this.audioContext.state === 'suspended') {
				void this.audioContext.resume().catch(() => {});
			}
			return this.audioContext;
		} catch (error) {
			logger.warn('Failed to create AudioContext for remote speaking analyser', {error});
			return null;
		}
	}
	attachIfApplicable(participant: Participant, publication: RemoteTrackPublication, track: RemoteTrack): void {
		this.ensureVisibilityListener();
		if (this.analyserSuspendedByVisibility) return;
		if (participant.isLocal) return;
		if (publication.source !== Track.Source.Microphone) return;
		if (track.kind !== Track.Kind.Audio) return;
		const audioTrack = track as RemoteAudioTrack;
		const mediaStreamTrack = audioTrack.mediaStreamTrack;
		if (!mediaStreamTrack || mediaStreamTrack.readyState === 'ended') return;
		const identity = participant.identity;
		const existing = this.analysers.get(identity);
		if (existing && existing.track === mediaStreamTrack) {
			return;
		}
		if (existing) {
			this.disposeHandle(existing);
		}
		const ctx = this.getAudioContext();
		if (!ctx) return;
		try {
			const source = ctx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
			const analyser = ctx.createAnalyser();
			analyser.fftSize = 512;
			analyser.smoothingTimeConstant = 0.2;
			source.connect(analyser);
			const handle: AnalyserHandle = {
				identity,
				track: mediaStreamTrack,
				source,
				analyser,
				samples: new Uint8Array(new ArrayBuffer(analyser.fftSize)),
				rafId: null,
				speaking: false,
				belowSinceMs: null,
				aboveSinceMs: null,
			};
			this.analysers.set(identity, handle);
			this.scheduleTick(handle);
		} catch (error) {
			logger.warn('Failed to attach remote analyser', {identity, error});
		}
	}
	detachByIdentity(identity: string): void {
		const handle = this.analysers.get(identity);
		if (!handle) return;
		this.disposeHandle(handle);
		this.analysers.delete(identity);
		VoiceParticipantManager.setAudioLevelSpeaking(identity, false);
	}
	detachIfTrackMatches(participant: Participant, publication: RemoteTrackPublication): void {
		if (participant.isLocal) return;
		if (publication.source !== Track.Source.Microphone) return;
		const handle = this.analysers.get(participant.identity);
		if (!handle) return;
		const track = publication.track as RemoteAudioTrack | undefined;
		if (!track || track.mediaStreamTrack === handle.track) {
			this.detachByIdentity(participant.identity);
		}
	}
	clear(): void {
		this.detachAllAnalysers();
		this.roomForRehydrate = null;
		if (this.audioContext) {
			void this.audioContext.close().catch(() => {});
			this.audioContext = null;
		}
	}
	hydrateFromRoom(room: Room): void {
		this.ensureVisibilityListener();
		this.roomForRehydrate = room;
		if (this.analyserSuspendedByVisibility) return;
		room.remoteParticipants.forEach((participant) => {
			participant.audioTrackPublications.forEach((publication) => {
				const remotePub = publication as RemoteTrackPublication;
				if (!remotePub.isSubscribed) return;
				const track = remotePub.track;
				if (!track) return;
				this.attachIfApplicable(participant, remotePub, track);
			});
		});
	}
	private disposeHandle(handle: AnalyserHandle): void {
		if (handle.rafId !== null && typeof window !== 'undefined') {
			window.cancelAnimationFrame(handle.rafId);
			handle.rafId = null;
		}
		try {
			handle.source.disconnect();
		} catch {}
		try {
			handle.analyser.disconnect();
		} catch {}
	}
	private scheduleTick(handle: AnalyserHandle): void {
		if (typeof window === 'undefined') return;
		handle.rafId = window.requestAnimationFrame(() => this.tick(handle));
	}
	private tick(handle: AnalyserHandle): void {
		if (this.analysers.get(handle.identity) !== handle) return;
		if (handle.track.readyState === 'ended') {
			this.detachByIdentity(handle.identity);
			return;
		}
		handle.analyser.getByteTimeDomainData(handle.samples);
		let sumSquares = 0;
		for (let i = 0; i < handle.samples.length; i++) {
			const normalized = (handle.samples[i]! - 128) / 128;
			sumSquares += normalized * normalized;
		}
		const rms = Math.sqrt(sumSquares / handle.samples.length);
		const threshold = getRemoteSpeakingThresholdRms(VoiceSettings.getVadThreshold());
		const now = performance.now();
		if (rms >= threshold) {
			handle.belowSinceMs = null;
			handle.aboveSinceMs ??= now;
			if (!handle.speaking && now - handle.aboveSinceMs >= SPEAKING_REMOTE_ATTACK_MS) {
				handle.speaking = true;
				VoiceParticipantManager.setAudioLevelSpeaking(handle.identity, true);
			}
		} else {
			handle.aboveSinceMs = null;
			handle.belowSinceMs ??= now;
			if (handle.speaking && now - handle.belowSinceMs >= SPEAKING_REMOTE_RELEASE_MS) {
				handle.speaking = false;
				VoiceParticipantManager.setAudioLevelSpeaking(handle.identity, false);
			}
		}
		this.scheduleTick(handle);
	}
}
const instance = new VoiceRemoteSpeakingLevelManager();
(
	window as typeof window & {
		_voiceRemoteSpeakingLevelManager?: VoiceRemoteSpeakingLevelManager;
	}
)._voiceRemoteSpeakingLevelManager = instance;
export default instance;
